import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ReactComponent as StarSvg } from "../../assets/icons/credit.svg";
import { ReactComponent as ArrowSvg } from "../../assets/icons/arrow.svg";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as CropIcon } from "../../assets/icons/crop.svg";
import {
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalTitle,
  Button,
  Row,
  ImageRow,
  ArrowContainer,
  UploadImageContainer,
  UploadImageButton,
  DeleteButton,
  CropButton,
  PreviewImage,
  AfterImageContainer,
  AfterImage,
  //   AfterRow,
  SelectGrid,
  SelectImageWrapper,
  SelectImage,
  CheckboxOverlay,
  ButtonsColumn,
  CancelButton,
} from "./AnonymizeModal.style";
import Modal from "../utils/Modal/Modal";
import CropModal from "../CropModal/CropModal";

// import ImgPreviewAfter4 from "../../assets/images/after4.webp";
// import ImgPreviewAfter3 from "../../assets/images/after3.webp";
// import ImgPreviewAfter2 from "../../assets/images/after2.webp";
import ImgPreviewAfter1 from "../../assets/images/after1.webp";
import ImgPreviewBefore from "../../assets/images/anonymize.webp";

import LottieIcon from "../LottieIcon/LottieIcon";
import deleteAnimation from "../../assets/animations/trash.json";
import radioButtonAnimation from "../../assets/animations/radioButton.json";

import heic2any from "heic2any";
import { MAX_IMAGE_SIZE } from "../../utils/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  generateAnonymize,
  selectCollectionsInCollectionGroup,
} from "../../store/slices/model/asyncThunks";
import {
  selectCollectionsInCollectionGroupPending,
  selectGenerateAnonymizedCollectionPending,
} from "../../store/slices/model/slice";
import ExplosionButton from "../ExplosionButton/ExplosionButton";

const MAX_SELECT = 4;

const AnonymizeModal = ({
  isOpen,
  onClose,
  collections,
  select,
  collectionGroup,
  overlayZIndex = 3101,
}) => {
  const fileInputRef = useRef();
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isOpenCropModal, setIsOpenCropModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const dispatch = useDispatch();
  const [isExploding, setIsExploding] = useState(false);
  const prevLoadingRef = useRef(false);

  const generateLoading = useSelector(
    selectGenerateAnonymizedCollectionPending,
  );

  const selectCollectionsLoading = useSelector(
    selectCollectionsInCollectionGroupPending,
  );

  useEffect(() => {
    if (prevLoadingRef.current && !generateLoading) {
      setIsExploding(true);

      const timer = setTimeout(() => {
        setIsExploding(false);
      }, 600);
      return () => clearTimeout(timer);
    }
    prevLoadingRef.current = generateLoading;
  }, [generateLoading]);

  const handleClose = () => {
    setSelectedImages([]);
    onClose();
  };

  const generateAnonymizeAction = async () => {
    const formData = new FormData();
    if (uploadedImage) {
      formData.append("image", uploadedImage.file);
    }

    dispatch(generateAnonymize(formData))
      .unwrap()
      .then(() => {
        toast.success("Generation started...");
        setTimeout(() => {
          onClose();
        }, 550);
        setUploadedImage(null);
      })
      .catch((err) => {
        console.log(err);
        toast.error(
          err ??
            "An error occurred while generating images. Please try again later.",
        );
      });
  };

  const handleFileChange = async (e) => {
    const { files } = e.target;

    let file = files[0];

    if (
      file &&
      [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
        "image/heic",
        "image/heif",
      ].includes(file.type)
    ) {
      if (file.size > MAX_IMAGE_SIZE) {
        toast.error("Image must be smaller than 30MB");
        return;
      }

      let blobToShow = file;

      if (file.type === "image/heic" || file.type === "image/heif") {
        try {
          const convertedBlob = await heic2any({
            blob: file,
            toType: "image/jpg",
            quality: 0.9,
          });
          blobToShow = convertedBlob;

          file = new File(
            [convertedBlob],
            file.name.replace(/\.[^/.]+$/, ".jpg"),
            {
              type: "image/jpg",
              lastModified: Date.now(),
            },
          );
        } catch (err) {
          console.error("HEIC → jpg conversion failed", err);
          return;
        }
      }

      setUploadedImage({ file, url: URL.createObjectURL(blobToShow) });
    } else {
      toast.error("Unsupported media type.");
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCropConfirm = (croppedImage) => {
    setUploadedImage((prev) => ({
      ...prev,
      url: croppedImage.url,
      file: new File([croppedImage.blob], prev.file.name, {
        type: "image/jpeg",
        lastModified: Date.now(),
      }),
    }));
  };

  const toggleSelectImage = (url) => {
    setSelectedImages((prev) => {
      if (prev.includes(url)) {
        return prev.filter((u) => u !== url);
      }
      if (prev.length >= MAX_SELECT) {
        return prev;
      }
      return [...prev, url];
    });
  };

  const selectCollectionsAction = async () => {
    dispatch(
      selectCollectionsInCollectionGroup({
        collections: selectedImages,
        collectionGroup,
      }),
    )
      .unwrap()
      .then(() => {
        onClose();
        setSelectedImages([]);
      })
      .catch((err) => {
        toast.error(
          "An error occurred selecting images. Please try again later.",
        );
      });
  };

  if (select) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        customOverlayStyles={{ zIndex: overlayZIndex }}
      >
        <ModalContainer>
          <ModalContent>
            <Row>
              <ModalTitle>Select Images</ModalTitle>
              <CloseButton onClick={handleClose}>
                <CloseSvg width={20} height={20} />
              </CloseButton>
            </Row>

            <SelectGrid>
              {(collections || []).map((collection) => {
                const url = collection.output.image.url;
                const id = collection._id;
                const isSelected = selectedImages.includes(id);
                return (
                  <SelectImageWrapper
                    key={id}
                    $selected={isSelected}
                    onClick={() => toggleSelectImage(id)}
                  >
                    <SelectImage src={url} alt={`Image`} />
                    <CheckboxOverlay>
                      <LottieIcon
                        animationData={radioButtonAnimation}
                        trigger="toggle-reverse"
                        speed={2}
                        isActive={isSelected}
                        size={22}
                        strokeColor={"#ffffff"}
                      />
                    </CheckboxOverlay>
                  </SelectImageWrapper>
                );
              })}
            </SelectGrid>

            <ButtonsColumn>
              <Button
                onClick={selectCollectionsAction}
                disabled={selectedImages.length === 0}
              >
                {selectCollectionsLoading ? (
                  <l-line-spinner
                    size="16"
                    stroke="1.25"
                    speed="1.5"
                    color="black"
                  />
                ) : (
                  <>
                    Select ({selectedImages.length}/{MAX_SELECT})
                  </>
                )}
              </Button>
              <CancelButton
                onClick={handleClose}
                disabled={selectedImages.length === 0}
              >
                Cancel
              </CancelButton>
            </ButtonsColumn>
          </ModalContent>
        </ModalContainer>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        customOverlayStyles={{ zIndex: overlayZIndex }}
      >
        <ModalContainer>
          <ModalContent>
            <Row>
              <ModalTitle>Anonymize</ModalTitle>
              <CloseButton onClick={handleClose}>
                <CloseSvg width={20} height={20} />
              </CloseButton>
            </Row>

            <ImageRow>
              <UploadImageContainer>
                {uploadedImage && (
                  <DeleteButton
                    disabled={generateLoading}
                    onClick={() => setUploadedImage(null)}
                  >
                    <LottieIcon
                      animationData={deleteAnimation}
                      trigger="hover"
                      size={24}
                    />
                  </DeleteButton>
                )}
                {uploadedImage && (
                  <CropButton
                    disabled={generateLoading}
                    onClick={() => setIsOpenCropModal(true)}
                  >
                    <CropIcon />
                  </CropButton>
                )}

                <PreviewImage
                  src={uploadedImage?.url || ImgPreviewBefore}
                  alt="Before"
                />

                <UploadImageButton
                  disabled={generateLoading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload Image
                </UploadImageButton>

                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </UploadImageContainer>

              <ArrowContainer>
                <ArrowSvg width={33} height={34} />
              </ArrowContainer>

              <AfterImageContainer>
                <AfterImage src={ImgPreviewAfter1} alt="After" />
              </AfterImageContainer>
            </ImageRow>
            <ExplosionButton
              disabled={!uploadedImage || generateLoading}
              onClick={generateAnonymizeAction}
              isLoading={generateLoading}
              isExploding={isExploding}
            >
              {generateLoading ? (
                <l-line-spinner
                  size="16"
                  stroke="1.25"
                  speed="1.5"
                  color="black"
                />
              ) : (
                <>
                  Generate <StarSvg />5
                </>
              )}
            </ExplosionButton>
          </ModalContent>
        </ModalContainer>
      </Modal>

      <CropModal
        isOpen={isOpenCropModal}
        onClose={() => setIsOpenCropModal(false)}
        onConfirm={handleCropConfirm}
        selectedImage={uploadedImage}
      />
    </>
  );
};

export default AnonymizeModal;
